import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Plus, Trash2, Warehouse as WarehouseIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

const EMPTY_FORM = { warehouse_name: "", full_address: "", city_id: "", city_name: "", latitude: "", longitude: "" };

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">{label}</label>
    {children}
  </div>
);

export default function WarehousesPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [cities, setCities]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);

  const fetchData = async () => {
    try {
      const [wRes, cRes] = await Promise.all([
        axios.get(`${API}/warehouses`, { withCredentials: true }),
        axios.get(`${API}/cities`, { withCredentials: true }),
      ]);
      setWarehouses(wRes.data);
      setCities(cRes.data);
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => setForm(EMPTY_FORM);

  const handleCitySelect = (cityId) => {
    const city = cities.find(c => c.id === cityId);
    setForm(f => ({ ...f, city_id: cityId, city_name: city?.city_name || "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.warehouse_name.trim()) { toast.error("Warehouse name is required."); return; }
    if (!form.full_address.trim())   { toast.error("Full address is required."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude:  form.latitude  ? parseFloat(form.latitude)  : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
      };
      const { data } = await axios.post(`${API}/warehouses`, payload, { withCredentials: true });
      setWarehouses(prev => [data, ...prev]);
      toast.success("Warehouse added successfully!");
      setShowAdd(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add warehouse.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this warehouse?")) return;
    try {
      await axios.delete(`${API}/warehouses/${id}`, { withCredentials: true });
      setWarehouses(prev => prev.filter(w => w.id !== id));
      toast.success("Warehouse deleted.");
    } catch {
      toast.error("Failed to delete warehouse.");
    }
  };

  return (
    <div className="space-y-5" data-testid="warehouses-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">Warehouses</h2>
          <p className="text-xs text-[#475569] mt-0.5">{warehouses.length} warehouse{warehouses.length !== 1 ? "s" : ""} registered</p>
        </div>
        <Button
          data-testid="add-warehouse-btn"
          onClick={() => { resetForm(); setShowAdd(true); }}
          className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]"
        >
          <Plus size={14} className="mr-1.5" />Add Warehouse
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : warehouses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <WarehouseIcon size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No warehouses yet. <button onClick={() => setShowAdd(true)} className="text-[#1B2A6B] font-semibold underline">Add your first warehouse</button></p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="warehouses-table">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                  {["#", "Warehouse Name", "Full Address", "City", "Coordinates", "Added On", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {warehouses.map((w, idx) => (
                  <tr key={w.id} data-testid={`warehouse-row-${idx}`} className={`border-b border-[#E2E8F0] ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}>
                    <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{idx + 1}</td>
                    <td className="px-4 py-3 font-semibold text-[#0F172A] whitespace-nowrap">{w.warehouse_name}</td>
                    <td className="px-4 py-3 text-[#475569] max-w-[220px]">
                      <p className="truncate" title={w.full_address}>{w.full_address}</p>
                    </td>
                    <td className="px-4 py-3 text-[#475569]">{w.city_name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap font-mono">
                      {w.latitude != null && w.longitude != null
                        ? `${w.latitude}, ${w.longitude}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap">{format(new Date(w.created_at), "dd MMM yyyy")}</td>
                    <td className="px-4 py-3">
                      <button data-testid={`delete-warehouse-${idx}`} onClick={() => handleDelete(w.id)} className="text-[#CBD5E1] hover:text-red-500 transition-colors p-1" title="Delete"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Warehouse Dialog */}
      <Dialog open={showAdd} onOpenChange={o => { if (!o) resetForm(); setShowAdd(o); }}>
        <DialogContent className="max-w-xl rounded-none p-0 gap-0 max-h-[90vh] overflow-y-auto">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700] sticky top-0 z-10">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Add New Warehouse</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Fill in the warehouse details below</p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">

              <div className="sm:col-span-2">
                <FormField label="Warehouse Name *">
                  <Input
                    value={form.warehouse_name}
                    onChange={e => setForm(f => ({ ...f, warehouse_name: e.target.value }))}
                    placeholder="e.g. Mumbai Central Warehouse"
                    data-testid="warehouse-name-input"
                    className={INPUT_CLS}
                    autoFocus
                  />
                </FormField>
              </div>

              <div className="sm:col-span-2">
                <FormField label="Full Address *">
                  <textarea
                    value={form.full_address}
                    onChange={e => setForm(f => ({ ...f, full_address: e.target.value }))}
                    placeholder="e.g. Plot 12, MIDC Industrial Area, Andheri East, Mumbai - 400093"
                    rows={3}
                    data-testid="warehouse-address-input"
                    className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] resize-none"
                  />
                </FormField>
              </div>

              <div className="sm:col-span-2">
                <FormField label="City">
                  <Select value={form.city_id} onValueChange={handleCitySelect}>
                    <SelectTrigger data-testid="warehouse-city-select" className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                      <SelectValue placeholder={cities.length === 0 ? "No cities available — add cities first" : "Select city"} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {cities.map(c => (
                        <SelectItem key={c.id} value={c.id} className="rounded-none">{c.city_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {cities.length === 0 && (
                    <p className="text-[11px] text-amber-600 mt-1">Go to Master Data → Cities to add cities first.</p>
                  )}
                </FormField>
              </div>

              <FormField label="Latitude">
                <Input
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))}
                  placeholder="e.g. 19.0760"
                  data-testid="warehouse-latitude-input"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Longitude">
                <Input
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))}
                  placeholder="e.g. 72.8777"
                  data-testid="warehouse-longitude-input"
                  className={INPUT_CLS}
                />
              </FormField>

            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={() => { resetForm(); setShowAdd(false); }} data-testid="warehouse-cancel-btn" className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">Cancel</button>
              <Button type="submit" data-testid="warehouse-save-btn" disabled={saving} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {saving ? "Saving..." : "Save Warehouse"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
